<template>
  <div class="oa-page">
    <Navigation />

    <div class="container py-4">
      <div class="d-flex align-items-center mb-4">
        <div>
          <h3 class="mb-0">
            <i :class="typeIcon(org.org_type)" class="me-2 text-primary"></i>{{ org.org_name || 'Organisation' }}
          </h3>
          <div class="small text-muted">
            {{ org.org_code }} · {{ org.org_type }} · <span :class="`badge bg-${statusColor(org.status)}`">{{ org.status }}</span>
          </div>
        </div>
        <button class="btn btn-sm btn-outline-secondary ms-auto" @click="refreshAll">
          <i class="mdi mdi-refresh me-1"></i> Refresh
        </button>
      </div>

      <!-- KPI strip -->
      <div class="row g-3 mb-4">
        <div class="col-md-3 col-6"><div class="oa-kpi">
          <div class="oa-kpi-label">Hospitals</div>
          <div class="oa-kpi-val">{{ (analytics.totals||{}).hospitals || hospitals.length }}</div>
          <div class="oa-kpi-sub">{{ activeHospCount }} active</div>
        </div></div>
        <div class="col-md-3 col-6"><div class="oa-kpi">
          <div class="oa-kpi-label">Patients</div>
          <div class="oa-kpi-val">{{ (analytics.totals||{}).patients || 0 }}</div>
          <div class="oa-kpi-sub">Lifetime total</div>
        </div></div>
        <div class="col-md-3 col-6"><div class="oa-kpi">
          <div class="oa-kpi-label">Visits (this month)</div>
          <div class="oa-kpi-val text-info">{{ (analytics.totals||{}).visits_mtd || 0 }}</div>
          <div class="oa-kpi-sub">{{ (analytics.totals||{}).visits_today || 0 }} today</div>
        </div></div>
        <div class="col-md-3 col-6"><div class="oa-kpi">
          <div class="oa-kpi-label">Collected (MTD)</div>
          <div class="oa-kpi-val text-success">{{ fmt((analytics.totals||{}).collected_mtd) }}</div>
          <div class="oa-kpi-sub">KES</div>
        </div></div>
      </div>

      <!-- Tabs -->
      <ul class="nav nav-tabs oa-tabs">
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='hospitals'}" href="javascript:void(0)" @click="tab='hospitals'">
          Hospitals <span class="badge bg-secondary ms-1">{{ hospitals.length }}</span>
        </a></li>
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='users'}" href="javascript:void(0)" @click="tab='users'">Users</a></li>
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='analytics'}" href="javascript:void(0)" @click="tab='analytics'">Analytics</a></li>
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='billing'}" href="javascript:void(0)" @click="tab='billing'">Billing</a></li>
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='profile'}" href="javascript:void(0)" @click="tab='profile'">Profile</a></li>
      </ul>

      <!-- HOSPITALS -->
      <div v-if="tab==='hospitals'" class="oa-panel">
        <div class="d-flex justify-content-between mb-3">
          <div class="text-muted small">All facilities under <strong>{{ org.org_name }}</strong></div>
          <button class="btn btn-sm btn-primary" @click="showAddHosp = true"><i class="mdi mdi-plus me-1"></i>Add hospital</button>
        </div>

        <div class="table-responsive">
          <table class="table table-hover oa-table">
            <thead><tr><th>Hospital</th><th>Code</th><th>Sub-county</th><th>Status</th><th class="text-end">Branches</th><th class="text-end">Active users</th><th></th></tr></thead>
            <tbody>
              <tr v-for="h in hospitals" :key="h.hospital_id">
                <td><strong>{{ h.hospital_name }}</strong></td>
                <td><code class="small">{{ h.hospital_code }}</code></td>
                <td>{{ h.sub_county || h.city || '—' }}<span v-if="h.ward" class="text-muted small"> · {{ h.ward }}</span></td>
                <td><span :class="`badge bg-${hStatusBadge(h.status)}`">{{ h.status }}</span></td>
                <td class="text-end">{{ h.branch_count }}</td>
                <td class="text-end">{{ h.active_users }}</td>
                <td class="text-nowrap">
                  <button class="btn btn-sm btn-outline-secondary me-1" @click="openEditHosp(h)" title="Edit hospital details"><i class="mdi mdi-pencil"></i></button>
                  <button class="btn btn-sm btn-outline-primary me-1" @click="openInvite(h)"><i class="mdi mdi-account-plus me-1"></i>Invite admin</button>
                  <button v-if="h.status === 'active'" class="btn btn-sm btn-outline-warning" @click="disableHospital(h)" title="Pause this hospital — staff lose access, data preserved">
                    <i class="mdi mdi-pause me-1"></i>Disable
                  </button>
                  <button v-else class="btn btn-sm btn-outline-success" @click="enableHospital(h)" title="Reactivate this hospital">
                    <i class="mdi mdi-play me-1"></i>Enable
                  </button>
                </td>
              </tr>
              <tr v-if="hospitals.length === 0"><td colspan="7" class="text-center text-muted py-4">No hospitals yet — add your first one!</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- USERS -->
      <div v-if="tab==='users'" class="oa-panel">
        <div class="text-muted small mb-3">Users across all your hospitals (read-only — per-hospital admins manage their own).</div>
        <div class="table-responsive">
          <table class="table oa-table">
            <thead><tr><th>Username</th><th>Name</th><th>Scope</th><th>Hospital</th><th>Role</th><th>Last login</th></tr></thead>
            <tbody>
              <tr v-for="u in users" :key="u.user_id">
                <td><strong>{{ u.username }}</strong></td>
                <td>{{ u.first_name }} {{ u.last_name }}</td>
                <td><span class="badge bg-light text-dark">{{ u.user_scope }}</span></td>
                <td>{{ u.hospital_name || '—' }}</td>
                <td><small>{{ u.role_name }}</small></td>
                <td><small class="text-muted">{{ u.last_login || 'Never' }}</small></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ANALYTICS -->
      <div v-if="tab==='analytics'" class="oa-panel">
        <div class="d-flex align-items-start justify-content-between">
          <p class="small text-muted mb-2">Aggregate counts and revenue across your facilities. <strong>No patient-level data is shown at the organisation level</strong> (DPA 2019 §51).</p>
          <button class="btn btn-sm btn-outline-primary flex-shrink-0 ms-2" @click="printReport"><i class="mdi mdi-printer me-1"></i>Print report</button>
        </div>
        <div class="row g-3 mb-3">
          <div class="col-md-3"><div class="oa-mini-kpi"><span>Beds total</span><strong>{{ (analytics.totals||{}).beds_total || 0 }}</strong></div></div>
          <div class="col-md-3"><div class="oa-mini-kpi"><span>Beds occupied</span><strong>{{ (analytics.totals||{}).beds_occupied || 0 }}</strong></div></div>
          <div class="col-md-3"><div class="oa-mini-kpi"><span>Occupancy</span><strong>{{ (analytics.totals||{}).occupancy_pct || 0 }}%</strong></div></div>
          <div class="col-md-3"><div class="oa-mini-kpi"><span>Active staff</span><strong>{{ (analytics.totals||{}).active_staff || 0 }}</strong></div></div>
        </div>
        <div class="oa-section-h">Per-hospital breakdown</div>
        <div class="table-responsive">
          <table class="table oa-table">
            <thead><tr><th>Hospital</th><th class="text-end">Patients</th><th class="text-end">Visits (MTD)</th><th class="text-end">Collected (MTD)</th><th class="text-end">Beds occ/total</th></tr></thead>
            <tbody>
              <tr v-for="h in (analytics.by_hospital||[])" :key="h.hospital_id">
                <td><strong>{{ h.hospital_name }}</strong> <code class="small text-muted">{{ h.hospital_code }}</code></td>
                <td class="text-end">{{ h.patient_count }}</td>
                <td class="text-end">{{ h.visits_mtd }}</td>
                <td class="text-end">{{ fmt(h.collected_mtd) }}</td>
                <td class="text-end">{{ h.beds_occupied }} / {{ h.beds_total }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- BILLING (read-only) -->
      <div v-if="tab==='billing'" class="oa-panel">
        <div class="alert alert-info small">
          <i class="mdi mdi-information me-1"></i> Payment terms and recording are managed manually by {{ $brand.name }}. If you have a question about your subscription, please contact us.
        </div>
        <div class="row g-3 mb-3" v-if="subscription">
          <div class="col-md-4"><div class="oa-mini-kpi"><span>Plan</span><strong>{{ subscription.plan_name }}</strong></div></div>
          <div class="col-md-4"><div class="oa-mini-kpi"><span>Cycle</span><strong>{{ subscription.billing_cycle }} · {{ fmt(subscription.cycle_amount_kes) }}</strong></div></div>
          <div class="col-md-4"><div class="oa-mini-kpi"><span>Paid through</span><strong>{{ subscription.paid_through_date || '—' }}</strong></div></div>
        </div>
        <div class="oa-section-h">Payment history</div>
        <div class="table-responsive">
          <table class="table oa-table">
            <thead><tr><th>Date paid</th><th>Period</th><th class="text-end">Amount</th><th>Method</th><th>Ref</th></tr></thead>
            <tbody>
              <tr v-for="p in payments" :key="p.payment_id">
                <td>{{ p.paid_on }}</td>
                <td class="small">{{ p.period_start }} → {{ p.period_end }}</td>
                <td class="text-end">{{ fmt(p.amount_kes) }}</td>
                <td>{{ p.payment_method }}</td>
                <td><code class="small">{{ p.reference_no || '—' }}</code></td>
              </tr>
              <tr v-if="payments.length === 0"><td colspan="5" class="text-center text-muted py-4">No payments recorded yet.</td></tr>
            </tbody>
          </table>
        </div>

        <div class="oa-section-h mt-4">Modules enabled for your organisation</div>
        <div class="row g-2">
          <div class="col-md-4 col-sm-6" v-for="f in features" :key="f.feature_code">
            <div class="oa-feature-row" :class="{disabled: f.is_enabled==0}">
              <i :class="f.is_enabled==1 ? 'mdi mdi-check-circle text-success' : 'mdi mdi-close-circle text-danger'"></i>
              <span>{{ f.feature_name }}</span>
              <small v-if="f.is_enabled==0" class="ms-auto text-danger">disabled</small>
            </div>
          </div>
        </div>
      </div>

      <!-- PROFILE -->
      <div v-if="tab==='profile'" class="oa-panel">
        <p class="small text-muted">Update your organisation profile. Some fields (legal name, KRA PIN, license number) are read-only and can only be changed by {{ $brand.name }} after re-verification.</p>
        <div class="row g-2">
          <div class="col-md-6"><label class="small">Organisation name</label><input v-model="profileEdit.org_name" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Website</label><input v-model="profileEdit.website_url" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Contact name</label><input v-model="profileEdit.primary_contact_name" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Logo URL</label><input v-model="profileEdit.logo_url" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Contact email</label><input v-model="profileEdit.primary_contact_email" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Contact phone</label><input v-model="profileEdit.primary_contact_phone" class="form-control form-control-sm"></div>
          <div class="col-12"><label class="small">Address</label><input v-model="profileEdit.address" class="form-control form-control-sm"></div>
        </div>
        <button class="btn btn-sm btn-primary mt-3" @click="saveProfile"><i class="mdi mdi-content-save me-1"></i>Save profile</button>

        <div class="oa-section-h mt-4">Read-only fields ({{ $brand.name }} managed)</div>
        <div class="row g-2 small">
          <div class="col-md-4"><b>Legal name:</b> {{ org.legal_name || '—' }}</div>
          <div class="col-md-4"><b>KRA PIN:</b> {{ org.kra_pin || '—' }}</div>
          <div class="col-md-4"><b>Registration no.:</b> {{ org.registration_number || '—' }}</div>
          <div class="col-md-4"><b>License no.:</b> {{ org.license_number || '—' }}</div>
          <div class="col-md-4"><b>License expiry:</b> {{ org.license_expiry || '—' }}</div>
          <div class="col-md-4"><b>Type:</b> {{ org.org_type }}</div>
        </div>
      </div>
    </div>

    <!-- ADD HOSPITAL MODAL -->
    <div v-if="showAddHosp" class="oa-modal-bg" @click.self="showAddHosp = false">
      <div class="oa-modal">
        <div class="d-flex justify-content-between mb-3"><h5>Add a hospital</h5><button class="btn-close" @click="showAddHosp = false"></button></div>
        <div class="row g-2">
          <div class="col-12"><label class="small">Hospital name <span class="text-danger">*</span></label><input v-model="newHosp.hospital_name" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Sub-county</label><input v-model="newHosp.sub_county" class="form-control form-control-sm" placeholder="e.g. Narok North"></div>
          <div class="col-md-6"><label class="small">Ward</label><input v-model="newHosp.ward" class="form-control form-control-sm" placeholder="e.g. Olorropil"></div>
          <div class="col-md-6"><label class="small">City / Town</label><input v-model="newHosp.city" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Country</label><input v-model="newHosp.country" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Email</label><input v-model="newHosp.email" type="email" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Phone</label><input v-model="newHosp.phone" class="form-control form-control-sm"></div>
          <div class="col-12"><label class="small">Address</label><input v-model="newHosp.address" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">KEPH level (optional)</label>
            <select v-model="newHosp.keph_level" class="form-select form-select-sm">
              <option value="">—</option>
              <option value="2">Level 2 — Dispensary</option>
              <option value="3">Level 3 — Health Centre</option>
              <option value="4">Level 4 — Sub-County Hospital</option>
              <option value="5">Level 5 — County Referral</option>
              <option value="6">Level 6 — National Referral</option>
            </select>
          </div>
          <div class="col-md-6"><label class="small">KMHFL code (optional)</label><input v-model="newHosp.kmhfl_code" class="form-control form-control-sm" placeholder="e.g. 12345"></div>
        </div>

        <!-- Sharon F#3 #29 — define satellite branches at hospital creation time.
             Satellites are for private hospital groups only; county facilities are
             registered as separate hospitals (QA: satellite rule for counties). -->
        <div v-if="org.org_type === 'county'" class="mt-3 pt-3 small text-muted" style="border-top:1px solid #e2e8f0;">
          <i class="mdi mdi-information-outline me-1"></i>Satellite branches are available to private hospital groups only.
          County facilities (dispensaries, health centres, sub-county hospitals) are added as separate hospitals under your county.
        </div>
        <div v-else class="mt-3 pt-3" style="border-top:1px solid #e2e8f0;">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong class="small">Satellite branches <span class="text-muted fw-normal">(optional — Main Branch is created automatically)</span></strong>
            <button class="btn btn-sm btn-outline-primary" @click="newHosp.branches.push({ branch_name:'', city:'', phone:'', email:'', address:'' })">
              <i class="mdi mdi-plus"></i> Add satellite
            </button>
          </div>
          <div v-for="(b, i) in newHosp.branches" :key="i" class="row g-2 mb-2 align-items-end" style="background:#f8fafc;padding:8px;border-radius:6px;">
            <div class="col-md-3"><label class="small">Branch name</label><input v-model="b.branch_name" class="form-control form-control-sm" placeholder="e.g. Kilgoris Sub-County"></div>
            <div class="col-md-2"><label class="small">City</label><input v-model="b.city" class="form-control form-control-sm"></div>
            <div class="col-md-2"><label class="small">Phone</label><input v-model="b.phone" class="form-control form-control-sm"></div>
            <div class="col-md-3"><label class="small">Email</label><input v-model="b.email" class="form-control form-control-sm"></div>
            <div class="col-md-1"><label class="small">Addr.</label><input v-model="b.address" class="form-control form-control-sm"></div>
            <div class="col-md-1"><button class="btn btn-sm btn-outline-danger w-100" @click="newHosp.branches.splice(i,1)" title="Remove"><i class="mdi mdi-close"></i></button></div>
          </div>
          <div v-if="newHosp.branches.length === 0" class="small text-muted">No satellites yet. Click <strong>+ Add satellite</strong> to define them now, or add later via the Branches module.</div>
        </div>

        <div v-if="addErr" class="alert alert-danger small py-2 mt-2">{{ addErr }}</div>
        <div class="d-flex gap-2 mt-3">
          <button class="btn btn-outline-secondary" @click="showAddHosp = false">Cancel</button>
          <button class="btn btn-primary flex-grow-1" @click="addHospital" :disabled="!newHosp.hospital_name">
            <i class="mdi mdi-plus me-1"></i>Add hospital{{ newHosp.branches.length > 0 ? ' + ' + newHosp.branches.length + ' satellite' + (newHosp.branches.length > 1 ? 's' : '') : '' }}
          </button>
        </div>
      </div>
    </div>

    <!-- EDIT HOSPITAL MODAL -->
    <div v-if="editHosp" class="oa-modal-bg" @click.self="editHosp = null">
      <div class="oa-modal">
        <div class="d-flex justify-content-between mb-3"><h5>Edit hospital details</h5><button class="btn-close" @click="editHosp = null"></button></div>
        <div class="row g-2">
          <div class="col-12"><label class="small">Hospital name <span class="text-danger">*</span></label><input v-model="editHosp.hospital_name" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Sub-county</label><input v-model="editHosp.sub_county" class="form-control form-control-sm" placeholder="e.g. Narok North"></div>
          <div class="col-md-6"><label class="small">Ward</label><input v-model="editHosp.ward" class="form-control form-control-sm" placeholder="e.g. Olorropil"></div>
          <div class="col-md-6"><label class="small">City / Town</label><input v-model="editHosp.city" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Country</label><input v-model="editHosp.country" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Email</label><input v-model="editHosp.email" type="email" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Phone</label><input v-model="editHosp.phone" class="form-control form-control-sm"></div>
          <div class="col-12"><label class="small">Address</label><input v-model="editHosp.address" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">KEPH level</label>
            <select v-model="editHosp.facility_level" class="form-select form-select-sm">
              <option value="">—</option>
              <option value="2">Level 2 — Dispensary</option>
              <option value="3">Level 3 — Health Centre</option>
              <option value="4">Level 4 — Sub-County Hospital</option>
              <option value="5">Level 5 — County Referral Hospital</option>
              <option value="6">Level 6 — National Referral Hospital</option>
            </select>
          </div>
          <div class="col-md-6"><label class="small">KMHFL code</label><input v-model="editHosp.kmfl_code" class="form-control form-control-sm" placeholder="e.g. 12345"></div>
        </div>
        <div v-if="editErr" class="alert alert-danger small py-2 mt-2">{{ editErr }}</div>
        <div class="d-flex gap-2 mt-3">
          <button class="btn btn-outline-secondary" @click="editHosp = null">Cancel</button>
          <button class="btn btn-primary flex-grow-1" @click="saveEditHosp" :disabled="!editHosp.hospital_name">
            <i class="mdi mdi-content-save me-1"></i>Save changes
          </button>
        </div>
      </div>
    </div>

    <!-- INVITE ADMIN MODAL -->
    <div v-if="inviteFor" class="oa-modal-bg" @click.self="inviteFor = null">
      <div class="oa-modal">
        <div class="d-flex justify-content-between mb-3">
          <h5>Invite admin for {{ inviteFor.hospital_name }}</h5>
          <button class="btn-close" @click="inviteFor = null"></button>
        </div>
        <p class="small text-muted">The user receives a temporary password and is prompted to change it on first login.</p>
        <div class="row g-2">
          <div class="col-md-6"><label class="small">First name *</label><input v-model="newUser.first_name" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Last name</label><input v-model="newUser.last_name" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Username *</label><input v-model="newUser.username" class="form-control form-control-sm" placeholder="e.g. mary.nailantei"></div>
          <div class="col-md-6"><label class="small">Email *</label><input v-model="newUser.email" type="email" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Phone</label><input v-model="newUser.phone" class="form-control form-control-sm"></div>
          <div class="col-md-6"><label class="small">Role</label>
            <select v-model="newUser.role_code" class="form-select form-select-sm">
              <option value="super_admin">Super Admin (full facility access)</option>
              <option value="hospital_admin">Hospital Admin</option>
              <option value="doctor">Doctor</option>
              <option value="nurse">Nurse</option>
              <option value="receptionist">Receptionist</option>
              <option value="billing_clerk">Billing Clerk</option>
            </select>
          </div>
          <div class="col-12"><label class="small">Temporary password *</label>
            <div class="input-group input-group-sm">
              <input :type="showPw?'text':'password'" v-model="newUser.password" class="form-control" placeholder="At least 8 characters">
              <button type="button" class="btn btn-outline-secondary" @click="showPw=!showPw"><i :class="showPw?'mdi mdi-eye-off':'mdi mdi-eye'"></i></button>
              <button type="button" class="btn btn-outline-secondary" @click="newUser.password = generatePw()">Generate</button>
            </div>
          </div>
        </div>
        <div v-if="inviteErr" class="alert alert-danger small py-2 mt-2">{{ inviteErr }}</div>
        <div class="d-flex gap-2 mt-3">
          <button class="btn btn-outline-secondary" @click="inviteFor = null">Cancel</button>
          <button class="btn btn-primary flex-grow-1" @click="invite" :disabled="!canInvite">
            <i class="mdi mdi-send me-1"></i>Send invitation
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { hms } from "@/api";
import Navigation from "@/components/hms-auth/Navigation.vue";

const auth = () => ({ headers: { Authorization: "Bearer " + localStorage.getItem("authToken") }});

export default {
  name: "OrgAdminDashboard",
  components: { Navigation },
  data() {
    return {
      tab: "hospitals",
      org: {}, subscription: null,
      hospitals: [], users: [], payments: [], features: [], analytics: {},
      profileEdit: {},
      showAddHosp: false, addErr: "",
      newHosp: { hospital_name: "", sub_county: "", ward: "", city: "", country: "Kenya", email: "", phone: "", address: "", keph_level: "", kmhfl_code: "", branches: [] },
      editHosp: null, editErr: "",
      inviteFor: null, inviteErr: "", showPw: false,
      newUser: { first_name: "", last_name: "", username: "", email: "", phone: "", role_code: "super_admin", password: "" },
    };
  },
  computed: {
    activeHospCount() { return this.hospitals.filter(h => h.status === "active").length; },
    canInvite() {
      const u = this.newUser;
      return u.first_name.trim() && u.username.trim() && u.email.trim() && u.password.length >= 8;
    }
  },
  mounted() {
    const u = JSON.parse(localStorage.getItem("userProfile") || "{}");
    if (u.role_code !== "org_admin") { this.$router.replace("/login"); return; }
    this.orgId = u.organization_id;
    this.refreshAll();
  },
  methods: {
    fmt(n) { return n == null ? "—" : Number(n).toLocaleString("en-KE", { maximumFractionDigits: 0 }); },
    statusColor(s) { return ({active:"success",pending_approval:"warning",warning:"warning",suspended:"danger",rejected:"secondary"})[s] || "secondary"; },
    typeIcon(t) { return ({county:"mdi mdi-domain",ngo:"mdi mdi-account-heart",private_group:"mdi mdi-hospital-building",individual:"mdi mdi-account-circle"})[t] || "mdi mdi-domain"; },
    generatePw() {
      const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
      return Array.from({length:12}, () => chars[Math.floor(Math.random()*chars.length)]).join("") + "!";
    },
    async post(fn, body) {
      const p = new URLSearchParams(); p.append("function", fn);
      Object.entries(body || {}).forEach(([k,v]) => p.append(k, v));
      const r = await axios.post(hms + "?function=" + fn, p, auth());
      if (!r.data || !r.data.success) throw new Error((r.data && r.data.message) || "Request failed");
      return r.data.data;
    },
    async refreshAll() {
      try {
        const d = await this.post("getOrganization", { organization_id: this.orgId });
        this.org = d.organization;
        this.subscription = d.subscription;
        this.profileEdit = {
          org_name: this.org.org_name,
          primary_contact_name: this.org.primary_contact_name,
          primary_contact_email: this.org.primary_contact_email,
          primary_contact_phone: this.org.primary_contact_phone,
          address: this.org.address,
          logo_url: this.org.logo_url,
          website_url: this.org.website_url
        };
        this.hospitals = (await this.post("listOrgHospitals")).rows || [];
        this.users     = (await this.post("listOrgUsers")).rows || [];
        this.payments  = (await this.post("listSubscriptionPayments")).rows || [];
        this.analytics = await this.post("getOrgAggregateAnalytics");
        this.features  = (await this.post("listOrgFeatureFlags")).features || [];
      } catch (e) { alert("Load failed: " + e.message); }
    },
    async saveProfile() {
      try { await this.post("updateOrganization", this.profileEdit); await this.refreshAll(); alert("Profile saved."); }
      catch (e) { alert(e.message); }
    },
    async addHospital() {
      this.addErr = "";
      try {
        const payload = Object.assign({}, this.newHosp, { branches: JSON.stringify(this.newHosp.branches || []) });
        const r = await this.post("createHospitalUnderOrg", payload);
        this.showAddHosp = false;
        this.newHosp = { hospital_name: "", sub_county: "", ward: "", city: "", country: "Kenya", email: "", phone: "", address: "", keph_level: "", kmhfl_code: "", branches: [] };
        await this.refreshAll();
        alert(`Hospital "${r.hospital_code}" added.`);
      } catch (e) { this.addErr = e.message; }
    },
    hStatusBadge(s) { return ({active:"success",inactive:"secondary",suspended:"danger"})[s] || "secondary"; },
    async disableHospital(h) {
      const reason = prompt(`Disable "${h.hospital_name}"?\n\nStaff will lose access immediately. Existing patient data is preserved.\n\nOptional reason (visible in audit log):`, "");
      if (reason === null) return; // cancel
      try {
        await this.post("setHospitalStatus", { hospital_id: h.hospital_id, status: "inactive", reason });
        await this.refreshAll();
      } catch (e) { alert(e.message); }
    },
    async enableHospital(h) {
      if (!confirm(`Re-enable "${h.hospital_name}"? Staff will be able to sign in again.`)) return;
      try {
        await this.post("setHospitalStatus", { hospital_id: h.hospital_id, status: "active", reason: "Re-enabled by org admin" });
        await this.refreshAll();
      } catch (e) { alert(e.message); }
    },
    openEditHosp(h) {
      this.editErr = "";
      this.editHosp = {
        hospital_id: h.hospital_id,
        hospital_name: h.hospital_name || "",
        sub_county: h.sub_county || "",
        ward: h.ward || "",
        city: h.city || "",
        country: h.country || "Kenya",
        email: h.email || "",
        phone: h.phone || "",
        address: h.address || "",
        facility_level: h.facility_level || "",
        kmfl_code: h.kmfl_code || ""
      };
    },
    async saveEditHosp() {
      this.editErr = "";
      try {
        await this.post("updateHospitalUnderOrg", this.editHosp);
        this.editHosp = null;
        await this.refreshAll();
      } catch (e) { this.editErr = e.message; }
    },
    printReport() { window.print(); },
    openInvite(h) {
      this.inviteFor = h;
      this.inviteErr = "";
      this.newUser = { first_name: "", last_name: "", username: "", email: "", phone: "", role_code: "super_admin", password: this.generatePw() };
    },
    async invite() {
      this.inviteErr = "";
      try {
        await this.post("inviteHospitalAdmin", { hospital_id: this.inviteFor.hospital_id, ...this.newUser });
        const pwd = this.newUser.password;
        const u   = this.newUser.username;
        this.inviteFor = null;
        await this.refreshAll();
        alert(`Invitation sent.\n\nUsername: ${u}\nTemp password: ${pwd}\n\nShare these credentials with the user securely. They will be forced to change the password on first login.`);
      } catch (e) { this.inviteErr = e.message; }
    }
  }
};
</script>

<style scoped>
.oa-page { background: var(--hms-bg2); min-height: 100vh; }
.oa-kpi { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 16px; height: 100%; }
.oa-kpi-label { font-size: 11px; text-transform: uppercase; color: var(--hms-s); letter-spacing: .5px; }
.oa-kpi-val { font-size: 28px; font-weight: 800; color: var(--hms-h); margin: 4px 0 2px; }
.oa-kpi-sub { font-size: 11px; color: var(--hms-s); }
.oa-mini-kpi { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 14px; display: flex; flex-direction: column; }
.oa-mini-kpi span { font-size: 11px; color: var(--hms-s); text-transform: uppercase; }
.oa-mini-kpi strong { font-size: 18px; color: var(--hms-h); }

.oa-tabs { border-bottom: 2px solid var(--hms-br); }
.oa-tabs .nav-link { color: var(--hms-s); border: none; border-bottom: 3px solid transparent; padding: 12px 18px; font-weight: 600; }
.oa-tabs .nav-link.active { color: var(--hms-primary); background: transparent; border-bottom-color: var(--hms-primary); }
.oa-panel { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-top: none; padding: 20px; border-radius: 0 0 12px 12px; }
.oa-table th { font-size: 12px; text-transform: uppercase; color: var(--hms-s); font-weight: 700; letter-spacing: .3px; }
.oa-section-h { font-size: 12px; text-transform: uppercase; color: var(--hms-s); font-weight: 700; letter-spacing: .5px; margin: 16px 0 8px; }

.oa-feature-row { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border: 1px solid var(--hms-br); border-radius: 8px; background: var(--hms-bg2); font-size: 13px; }
.oa-feature-row.disabled { opacity: .55; }

.oa-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 20px; }
.oa-modal { background: var(--hms-bg1); border-radius: 12px; padding: 24px; width: min(560px, 100vw); max-height: 90vh; overflow-y: auto; }

label.small { font-size: 11px; font-weight: 600; color: var(--hms-h); margin-bottom: 2px; }
</style>
